import { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { createRowStore } from './row-index-store';

interface ChosenRow {
    rowKey: string;
    /** The item that the row shows, as the list names its open item. */
    itemKey: string | null;
}

interface Options {
    /** Each row's key, from uniqueRowKeys. */
    rowKeys: string[];
    /** The item whose page is open. */
    openItemKey: string | null | undefined;
    /**
     * The row with the highlight while no row is chosen, or -1: as a rule the
     * open item's first row, which for a favourite is its row in Favourites.
     */
    noChoiceIndex: () => number;
    /**
     * A choice holds within one scope, such as one search text. A new scope
     * drops it.
     */
    choiceScope?: unknown;
}

/**
 * The highlight of a list in which one item can show in two rows, such as a
 * favourite. It is on the row the user chose while that row is in the list.
 * While it is not, as when Favourites is folded, the row from noChoiceIndex
 * has it. The rows read it from the store, so a new highlight renders only
 * the row that loses it and the row that gets it, never the list.
 */
const useRowSelection = ({
    rowKeys,
    openItemKey,
    noChoiceIndex,
    choiceScope,
}: Options) => {
    // This is one store for the life of the list. A new highlight changes the
    // row that the store holds, not the store object. So the hooks that list
    // the store do not run again because of it. It holds the row's key, so the
    // highlight stays on its row when the list sorts again.
    const [selectionStore] = useState(() => createRowStore<string>());
    const chosenRowRef = useRef<ChosenRow | null>(null);
    const lastOpenItemKeyRef = useRef(openItemKey);
    const lastChoiceScopeRef = useRef(choiceScope);

    // The row the user chose, while it is in the list; else -1.
    const findChosenIndex = useCallback(
        () =>
            chosenRowRef.current === null
                ? -1
                : rowKeys.indexOf(chosenRowRef.current.rowKey),
        [rowKeys],
    );

    const findSelectedIndex = useCallback(() => {
        const chosenIndex = findChosenIndex();
        return chosenIndex === -1 ? noChoiceIndex() : chosenIndex;
    }, [findChosenIndex, noChoiceIndex]);

    // A page that opens another way, such as by Back or Forward, drops a
    // choice of another item. A click opens the page of the item it chose, so
    // that choice stays. While no page of an item is open, as on the
    // Overview, it stays too. The choice is settled before the store gets the
    // highlight, in one effect, so the store follows a dropped choice at once.
    useLayoutEffect(() => {
        const anotherItemOpened =
            lastOpenItemKeyRef.current !== openItemKey &&
            !!openItemKey &&
            chosenRowRef.current?.itemKey !== openItemKey;
        const scopeChanged = lastChoiceScopeRef.current !== choiceScope;
        lastOpenItemKeyRef.current = openItemKey;
        lastChoiceScopeRef.current = choiceScope;
        if (anotherItemOpened || scopeChanged) {
            chosenRowRef.current = null;
        }

        const index = findSelectedIndex();
        selectionStore.set(index === -1 ? null : rowKeys[index]);
    }, [selectionStore, findSelectedIndex, rowKeys, openItemKey, choiceScope]);

    const chooseRow = useCallback(
        (index: number, itemKey: string | null) => {
            const rowKey = rowKeys[index] ?? null;
            chosenRowRef.current = rowKey === null ? null : { rowKey, itemKey };
            selectionStore.set(rowKey);
        },
        [rowKeys, selectionStore],
    );

    // Gives the highlight back to the row it has with no choice, such as the
    // row of the page that is open.
    const dropChoice = useCallback(() => {
        chosenRowRef.current = null;
        const index = noChoiceIndex();
        selectionStore.set(index === -1 ? null : rowKeys[index]);
    }, [noChoiceIndex, rowKeys, selectionStore]);

    return {
        selectionStore,
        findChosenIndex,
        findSelectedIndex,
        chooseRow,
        dropChoice,
    };
};

export default useRowSelection;
